import './Quote.css';

import { WorkflowVersion } from '@foxden/version-controller-client';
import React, { useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { z } from 'zod';

import { useAuth } from '../../auth/AuthProvider';
import { PaymentClient } from '../../backend-client/paymentBackend';
import { PaymentGraphqlClient } from '../../backend-client/paymentGraphqlBackend';
import { RatingQuotingClient } from '../../backend-client/ratingQuotingBackend';
import Loading from '../../components/Loading/Loading';
import { BusinessInfo } from '../../generated/graphql';
import { UserPaymentPlanOptionByQuoteId } from '../../generated/payment-graphql';
import {
  AdditionalInsured as AdditionalInsuredInfo,
  CoveragesData,
  GetQuoteOk,
  UpdateCoverageInput,
  UpdateCoverageOutput
} from '../../generated/rating-quoting-graphql';
import { Context, ContextValue } from '../../utils/store';
import ErrorPage from '../Error/Error';
import AdditionalInsured from './components/AdditionalInsured';
import BuyNowButton from './components/BuyNowButton';
import Coverages from './components/Coverages';
import CustomerAgreement from './components/CustomerAgreement';
import Premium from './components/Premium';
import Summary from './components/Summary';

export interface CoveragesDataInput {
  name: string;
  chooseCoverage: boolean;
  finalPremium: number;
  limits: Record<string, number>;
}

interface QuoteProps {
  applicationId: string;
  quoteData: GetQuoteOk;
  versionData?: WorkflowVersion;
  shouldShowCustomerAgreement?: boolean;
  isTnCLinkSent?: boolean;
  businessInfo: BusinessInfo;
}

const additionalInsuedInfoSchema = z.object({
  name: z.string().min(1),
  address: z.object({
    street: z.string().min(1),
    postalCode: z.string().min(5),
    province: z.string().min(2),
    city: z.string().min(1),
    address2: z.string().nullable().optional()
  }),
  email: z
    .string()
    .refine((value) => value === '')
    .or(z.string().email())
});

// This component handles rendering of active quotes (quoteData is always defined)
// Expired quote handling is done in index.tsx

const Quote: React.FC<QuoteProps> = (props) => {
  const context: ContextValue | null = useContext(Context);

  const {
    applicationId,
    quoteData,
    versionData,
    shouldShowCustomerAgreement,
    isTnCLinkSent: isTnCLinkSentProp,
    businessInfo
  } = props;

  const { isAuthenticated, isLoading: isAuthProviderLoading } = useAuth();

  const isCustomerAgreementActive =
    shouldShowCustomerAgreement ??
    (context ? context.isCustomerAgreementActive : false);

  const [quoteResponse, setQuoteResponse] = useState(quoteData);

  const [country, setCountry] = useState('');
  const [province, setProvince] = useState('');

  const [premium, setPremium] = useState(quoteData ? quoteData.premium : 0);

  const [additionalInsuredInfoList, setAdditionalInsuredInfoList] = useState<
    AdditionalInsuredInfo[] | undefined
  >(undefined);
  const [
    fillAllAdditionalInsuredInfo,
    setFillAllAdditionalInsuredInfo
  ] = useState<boolean | undefined>(undefined);
  let additionalInsuredCoverage: CoveragesData | undefined = undefined;
  if (quoteResponse) {
    quoteResponse.coveragesData.forEach((coverage) => {
      if (coverage.name === 'additionalInsured') {
        additionalInsuredCoverage = coverage;
        return;
      }
    });
  }
  const enableAdditionalInsured = additionalInsuredCoverage ? true : false;

  const isEndorsement = quoteData?.transactionType === 'Endorsement';

  const [
    updateCoverageData,
    setUpdateCoverageData
  ] = useState<UpdateCoverageOutput>();
  const [
    isUpdateCoverageByRatingQuotingLoading,
    setIsUpdateCoverageByRatingQuotingLoading
  ] = useState<boolean>(false);
  const [updateCoverageError, setUpdateCoverageError] = useState();

  const [
    resetRecommendationData,
    setResetRecommendationData
  ] = useState<UpdateCoverageOutput>();
  const [
    isResetCoverageRecommendationByRatingQuotingLoading,
    setIsResetCoverageRecommendationByRatingQuotingLoading
  ] = useState<boolean>(false);
  const [resetRecommendationError, setResetRecommendationError] = useState();

  const [quoteId, setQuoteId] = useState<string | undefined>(undefined); // this quoteId is getting from getQuoteOK response

  const [paymentOptionData, setPaymentOptionData] = useState<
    Array<UserPaymentPlanOptionByQuoteId>
  >();
  const [
    isGetUserPaymentPlanOptionByQuoteIdLoading,
    setIsGetUserPaymentPlanOptionByQuoteIdLoading
  ] = useState<boolean>(false);
  const [
    getUserPaymentPlanOptionByQuoteIdError,
    setGetUserPaymentPlanOptionByQuoteIdError
  ] = useState();
  const [
    paymentGraphqlClient,
    setPaymentGraphqlClient
  ] = useState<PaymentGraphqlClient | null>(null);

  useEffect(() => {
    if (quoteId && versionData) {
      const client = new PaymentGraphqlClient(
        versionData.paymentBackendVersion,
        quoteId
      );
      setPaymentGraphqlClient(client);
    }
  }, [quoteId, versionData]);

  useEffect(() => window.scrollTo(0, 0), []);

  useEffect(() => {
    if (context && businessInfo?.companyName) {
      const { setHeaderTitle } = context;
      setHeaderTitle(businessInfo.companyName);
    }
  }, [businessInfo?.companyName, context]);

  useEffect(() => {
    // assign quoteId to state
    const { region, quoteId: id } = quoteResponse;
    setQuoteId(id);

    setCountry(region == 'Canada' ? 'Canada' : 'United States of America');
    // for now, consider "Canada" as province for data record, other US states will use individual state name
    setProvince(region);
  }, [quoteResponse, country, province]);

  useEffect(() => {
    const defaultAdditionalInsuredInfoList = quoteData?.additionalInsuredInfoList || [
      { name: '', address: undefined, email: '' }
    ];
    // only set the AdditionalInsured default value for valid cases
    if (enableAdditionalInsured && additionalInsuredCoverage?.chooseCoverage) {
      setAdditionalInsuredInfoList(defaultAdditionalInsuredInfoList);
    }
  }, [additionalInsuredCoverage, enableAdditionalInsured, quoteData]);

  useEffect(() => {
    if (resetRecommendationData && quoteResponse) {
      setQuoteResponse({
        ...quoteResponse,
        coveragesData: resetRecommendationData.coveragesData,
        quoteId: resetRecommendationData.quoteId,
        premium: resetRecommendationData.finalPremium
      });
      setPremium(resetRecommendationData.finalPremium);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetRecommendationData]);

  useEffect(() => {
    if (updateCoverageData && quoteResponse) {
      setQuoteResponse({
        ...quoteResponse,
        coveragesData: updateCoverageData.coveragesData,
        quoteId: updateCoverageData.quoteId,
        premium: updateCoverageData.finalPremium
      });
      setPremium(updateCoverageData.finalPremium);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCoverageData]);

  useEffect(() => {
    if (!isEndorsement && quoteId !== undefined) {
      setIsGetUserPaymentPlanOptionByQuoteIdLoading(true);

      const paymentClient = new PaymentClient(
        versionData?.paymentBackendVersion
      );

      paymentClient
        .getUserPaymentPlanOptionByQuoteId(quoteId)
        .then((data) => {
          setPaymentOptionData(data);
          setIsGetUserPaymentPlanOptionByQuoteIdLoading(false);
        })
        .catch((err) => {
          setGetUserPaymentPlanOptionByQuoteIdError(err);
          setIsGetUserPaymentPlanOptionByQuoteIdLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, versionData, isEndorsement]);

  const onUpdateCoverage = async (input: UpdateCoverageInput) => {
    // call backend to update coverage and get a new premium
    if (versionData) {
      setIsUpdateCoverageByRatingQuotingLoading(true);

      const ratingQuotingClient = new RatingQuotingClient(
        versionData.quoteBackendVersion
      );
      ratingQuotingClient
        .updateCoverage(input)
        .then((data) => {
          setUpdateCoverageData(data);
          setIsUpdateCoverageByRatingQuotingLoading(false);
        })
        .catch((err) => {
          setUpdateCoverageError(err);
          setIsUpdateCoverageByRatingQuotingLoading(false);
        });
    }
  };

  const onResetRecommendation = async () => {
    const resetCoverageInput = {
      quoteId: quoteResponse.quoteId,
      applicationId,
      region: quoteResponse.region
    };

    if (versionData) {
      setIsResetCoverageRecommendationByRatingQuotingLoading(true);
      const ratingQuotingClient = new RatingQuotingClient(
        versionData.quoteBackendVersion
      );
      ratingQuotingClient
        .resetCoverageRecommendation(resetCoverageInput)
        .then((data) => {
          setResetRecommendationData(data);
          setIsResetCoverageRecommendationByRatingQuotingLoading(false);
        })
        .catch((err) => {
          setResetRecommendationError(err);
          setIsResetCoverageRecommendationByRatingQuotingLoading(false);
        });
    }
  };

  if (resetRecommendationError || updateCoverageError) {
    return <ErrorPage />;
  }

  if (
    isUpdateCoverageByRatingQuotingLoading ||
    isResetCoverageRecommendationByRatingQuotingLoading ||
    isGetUserPaymentPlanOptionByQuoteIdLoading ||
    !quoteId ||
    isAuthProviderLoading
  ) {
    return <Loading />;
  }

  if (getUserPaymentPlanOptionByQuoteIdError) {
    throw Error('Backend error');
  }

  const totalPayable = paymentOptionData
    ? paymentOptionData[0].totalPayable
    : premium;

  const showSendQuoteButton = isAuthenticated;
  const quoteNumber = quoteResponse?.quoteNumber || '';

  const validateAdditionalInsured = () => {
    let validataionResult = true;
    if (
      additionalInsuredCoverage?.['chooseCoverage'] &&
      additionalInsuredInfoList
    ) {
      additionalInsuredInfoList.forEach((info) => {
        try {
          additionalInsuedInfoSchema.parse(info);
        } catch (e) {
          setFillAllAdditionalInsuredInfo(false);
          validataionResult = false;
          return;
        }
      });
    }
    return validataionResult;
  };

  if (premium <= 0 && !isEndorsement) {
    return (
      <Redirect
        to={{
          pathname: '/complete',
          state: {
            country,
            province
          }
        }}
      />
    );
  }

  return (
    <div className="relative page-container font-raleway lg:my-4">
      <div className="mt-1 quote-box">
        <div className="quote-header md:hidden text-center mb-2">
          <p className="text-2xl text-navy-dark font-bold">
            {isCustomerAgreementActive
              ? 'Client Agreements'
              : 'Tailored Quote For'}
            :
          </p>
          <p className="text-xl font-medium">{businessInfo?.companyName}</p>
        </div>
        <Premium
          yearlyPremium={premium}
          isEndorsement={isEndorsement}
          paymentOptionData={paymentOptionData}
        />
        <Summary
          summaryInput={quoteResponse}
          yearlyPremium={premium}
          totalPayable={totalPayable}
          isEndorsement={isEndorsement}
          carrierPartner={quoteResponse?.carrierPartner}
        />
        {isCustomerAgreementActive && quoteId ? (
          <CustomerAgreement
            quoteId={quoteId}
            customerName={businessInfo?.customerName || ''}
            email={businessInfo?.email || ''}
            paymentGraphqlClient={paymentGraphqlClient}
            isTnCLinkSent={isTnCLinkSentProp ?? false}
          />
        ) : (
          <Coverages
            coverageInput={quoteResponse}
            onUpdateCoverage={onUpdateCoverage}
            onResetRecommendation={onResetRecommendation}
            isEndorsement={isEndorsement}
            applicationId={applicationId}
          />
        )}
        {quoteResponse &&
        additionalInsuredCoverage &&
        !isCustomerAgreementActive ? (
          <AdditionalInsured
            coverageData={additionalInsuredCoverage}
            onUpdateCoverage={onUpdateCoverage}
            region={quoteResponse.region}
            quoteId={quoteResponse.quoteId}
            applicationId={applicationId}
            additionalInsuredInfoList={additionalInsuredInfoList}
            setAdditionalInsuredInfoList={setAdditionalInsuredInfoList}
            fillAllAdditionalInsuredInfo={fillAllAdditionalInsuredInfo}
            setFillAllAdditionalInsuredInfo={setFillAllAdditionalInsuredInfo}
            disabled={quoteResponse.disableAdditionalInsuredCheckbox}
          />
        ) : null}
        <BuyNowButton
          quoteId={quoteId}
          applicationId={applicationId}
          showSendQuoteButton={showSendQuoteButton}
          country={country}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          businessInfo={businessInfo!}
          disabled={false}
          setQuoteId={setQuoteId}
          additionalInsuredInfoList={additionalInsuredInfoList}
          validateAdditionalInsured={validateAdditionalInsured}
          versionData={versionData}
          quoteNumber={quoteNumber}
          isCustomerAgreementActive={isCustomerAgreementActive}
          isAuthenticated={isAuthenticated}
          isAuthProviderLoading={isAuthProviderLoading}
          paymentGraphqlClient={paymentGraphqlClient}
          transactionType={quoteData.transactionType}
          quoteExpireDate={quoteData.quoteExpireDate}
        />
      </div>
    </div>
  );
};

export default Quote;
